import type { BlackfiskResponse } from "@/services/blackfisk/client";
import type { CacheAxiosResponse, CacheRequestConfig } from "axios-cache-interceptor";

import { useLogger } from "@blackfisk/vue/composables/useLogger";

import { BlackfiskClient, BlackfiskRequestError } from "@/services/blackfisk/client";

const logger = useLogger("client-frontend", "http-service");

export interface EntityServiceResponseError {
  message: string;
  code: string | number | null;
  reference: string | number | null;
}
export interface EntityServiceResponse {
  errors: EntityServiceResponseError[];
}

export interface BulkResponse<T> {
  data: T[];
  errors: EntityServiceResponseError[];
}
export const formatErrors = (
  errors: EntityServiceResponseError[],
): {
  errorMessage: string;
  errorDetail: string;
} => {
  const errorsReduced = errors.reduce<
    Record<string, { count: number; messages: string[]; references: (string | number)[] }>
  >((accumulator, error) => {
    if (error.code) {
      if (accumulator[error.code] === undefined) {
        accumulator[error.code] = {
          count: 0,
          messages: [],
          references: [],
        };
      }
      const errorCodeAccumulator = accumulator?.[error.code];
      if (errorCodeAccumulator) {
        errorCodeAccumulator.count += 1;
        errorCodeAccumulator.messages.push(error.message);
        if (error.reference) {
          errorCodeAccumulator.references.push(error.reference);
        }
      }
    }
    return accumulator;
  }, {});
  const { errorMessage, errorDetail } = Object.entries(errorsReduced).reduce<{
    errorMessage: string;
    errorDetail: string;
  }>(
    (accumulator, [code, detail]) => {
      accumulator.errorMessage += `${detail.count} x ${code} `;
      accumulator.errorDetail += `${Array.from(new Set(detail.messages)).join(", ")}`;
      return accumulator;
    },
    {
      errorMessage: "",
      errorDetail: "",
    },
  );
  return {
    errorMessage,
    errorDetail,
  };
};
export const parsedGeneralError = (
  error: string[],
  status: number | null = null,
): EntityServiceResponseError[] => {
  return error.map((mapError) => ({
    code: status,
    message: mapError,
    reference: null,
  }));
};
export const parseBlackfiskBulkResponse = (
  response: BlackfiskResponse<{ errors: EntityServiceResponseError[] }[]>,
): EntityServiceResponseError[] => {
  return response.data.reduce<EntityServiceResponseError[]>((accumulator, iteratee) => {
    iteratee.errors.forEach((error) => {
      accumulator.push(error);
    });
    return accumulator;
  }, []);
};

export const parseBulkCreateResponse = <T>(
  response: BlackfiskResponse<BulkResponse<T>[]>,
): BulkResponse<T> => {
  const item = response.data?.[0];
  return {
    data: item?.data ?? [],
    errors: item?.errors ?? [],
  };
};
export const blackfiskAxiosRequest = async <R, T = R>(
  requestConfig: CacheRequestConfig,
  dataParser: (response: BlackfiskResponse<R>) => T,
): Promise<{
  errors: EntityServiceResponseError[];
  data: T | null;
  cached: boolean;
  response: CacheAxiosResponse<BlackfiskResponse<R>, unknown> | null;
}> => {
  let errors: EntityServiceResponseError[] = [];
  try {
    const ClientResponse = await BlackfiskClient<BlackfiskResponse<R>>(requestConfig);
    if (!ClientResponse) {
      return {
        errors: [
          {
            code: null,
            message: "Server could not be reached",
            reference: null,
          },
        ],
        data: null,
        cached: false,
        response: null,
      };
    }
    const { error = [] } = ClientResponse.data;
    if (error) {
      errors = [...errors, ...parsedGeneralError(error, ClientResponse.status ?? null)];
    }
    const data = dataParser(ClientResponse.data);

    return {
      errors,
      data,
      cached: ClientResponse.cached,
      response: ClientResponse,
    };
  } catch (err) {
    if (err instanceof BlackfiskRequestError) {
      if (err.message === "Request cancelled." || err.message === "Request timed out.") {
        throw err;
      }
      errors.push({
        message: err.message,
        code: err.status,
        reference: null,
      });
    } else {
      logger.error("Unexpected error: {error}", { error: err });
      errors.push({
        message: "An unexpected error occurred.",
        code: null,
        reference: null,
      });
    }
  }
  return {
    errors,
    data: null,
    cached: false,
    response: null,
  };
};
